import React, { useEffect, useState } from 'react';
import PageWrapper from '@/components/utils/PageWrapper';
import AnimatedElement from '@/components/utils/AnimatedElement';

interface BlogPost {
  title: string;
  link: string;
  pubDate: string;
  content: string;
  guid: string;
}

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Using a CORS proxy to fetch the RSS feed
        const proxyUrl = 'https://api.allorigins.win/raw?url=';
        const feedUrl = 'https://fictionalstream.substack.com/feed.xml';
        const response = await fetch(proxyUrl + encodeURIComponent(feedUrl));
        
        if (!response.ok) {
          throw new Error('Failed to fetch blog posts');
        }

        const text = await response.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, 'text/xml');
        const items = xmlDoc.querySelectorAll('item');
        
        const parsedPosts = Array.from(items)
          .slice(0, 10) // Get only the 10 most recent posts
          .map(item => ({
            title: item.querySelector('title')?.textContent || '',
            link: item.querySelector('link')?.textContent || '',
            pubDate: new Date(item.querySelector('pubDate')?.textContent || '').toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }),
            content: item.querySelector('description')?.textContent || '',
            guid: item.querySelector('guid')?.textContent || ''
          }));

        setPosts(parsedPosts);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  return (
    <PageWrapper title="Blog" subtitle="Explore our latest articles and insights on AI, data science, and IoT technologies.">
      <div className="container mx-auto px-6 py-12">
        <AnimatedElement animation="slideUp" delay={0.4} duration={0.8}>
          <div className="glass-card p-6 rounded-xl shadow-prominent mb-8">
            {loading ? (
              <div className="flex items-center justify-center min-h-[200px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="text-center text-red-500 p-4">
                <p>{error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : posts.length > 0 ? (
              <div className="space-y-6">
                {posts.map((post) => (
                  <article key={post.guid} className="border-b border-gray-200 pb-6 last:border-0">
                    <h2 className="text-xl font-semibold mb-2">
                      <a 
                        href={post.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors"
                      >
                        {post.title}
                      </a>
                    </h2>
                    <p className="text-sm text-gray-500 mb-3">{post.pubDate}</p>
                    <div 
                      className="text-gray-600 mb-4 line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: post.content }}
                    />
                    <a 
                      href={post.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-primary hover:text-primary/80 font-medium"
                    >
                      Read More
                      <svg 
                        className="w-4 h-4 ml-1" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </a>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 p-4">
                No posts found. Please check back later.
              </div>
            )}
          </div>
        </AnimatedElement>

        <AnimatedElement animation="slideUp" delay={0.6} duration={0.8}>
          <div className="glass-card p-6 rounded-xl shadow-prominent">
            <iframe 
              src="https://fictionalstream.substack.com/embed" 
              width="100%" 
              height="320" 
              style={{ border: '1px solid #EEE', background: 'white' }} 
              frameBorder="0" 
              scrolling="no"
            />
          </div>
        </AnimatedElement>
      </div>
    </PageWrapper>
  );
};

export default Blog; 